import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { meetingAPI, recordingAPI } from '../services/api';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import './MeetingDetails.css';

function MeetingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMeetingDetails();
  }, [id]);

  const loadMeetingDetails = async () => {
  try {
    const [meetingRes, participantsRes, recordingsRes] = await Promise.all([
      meetingAPI.get(id),
      meetingAPI.getParticipantsHistory(id),  // Changed from getParticipants
      recordingAPI.listByMeeting(id)
    ]);

    setMeeting(meetingRes.data.meeting);
    setParticipants([
      ...participantsRes.data.participants,
      ...participantsRes.data.guests.map(g => ({
        ...g,
        name: g.guest_name,
        is_host: false
      }))
    ]);
    setRecordings(recordingsRes.data.recordings);
    setStats(participantsRes.data.stats);
  } catch (error) {
    toast.error('Failed to load meeting details');
  } finally {
    setLoading(false);
  }
};

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const handleCopyInvite = () => {
    navigator.clipboard.writeText(meeting.invite_url);
    toast.success('Invite link copied!');
  };

  if (loading) {
    return (
      <div className="meeting-details-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Loading meeting details...</p>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="meeting-details-container">
        <div className="error-screen">
          <h2>Meeting not found</h2>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="meeting-details-container">
      <header className="details-header">
        <button onClick={() => navigate('/dashboard')} className="btn-back">
          ← Back to Dashboard
        </button>
      </header>

      <div className="details-content">
        <motion.div
          className="meeting-header-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1>🎤 {meeting.title}</h1>
          <div className="meeting-meta">
            <span className={`status-badge badge-${meeting.status}`}>
              {meeting.status === 'active' && 'Live'}
              {meeting.status === 'scheduled' && 'Scheduled'}
              {meeting.status === 'ended' && 'Ended'}
            </span>
            <span>📅 {new Date(meeting.created_at).toLocaleString()}</span>
          </div>
        </motion.div>

        <div className="details-grid">
          {/* Meeting Info Card */}
          <motion.div
            className="detail-card card-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <h3>📋 Meeting Information</h3>
            <div className="info-list">
              <div className="info-item">
                <span className="info-label">Host</span>
                <span className="info-value">👤 {meeting.host_name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Meeting Code</span>
                <span className="info-value mono">{meeting.meeting_code || meeting.room_id}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className="info-value" style={{textTransform: 'capitalize'}}>{meeting.status}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Total Participants</span>
                <span className="info-value">{stats?.total_participants || 0}</span>
              </div>
            </div>

            {meeting.status !== 'ended' && (
              <div className="invite-section">
                <label>🔗 Invite Link</label>
                <div className="invite-input-group">
                  <input
                    type="text"
                    value={meeting.invite_url}
                    readOnly
                    className="invite-input-small"
                  />
                  <button onClick={handleCopyInvite} className="btn-copy-small">
                    📋 Copy
                  </button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Participant Stats Card */}
          <motion.div
            className="detail-card card-stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h3>📊 Participant Statistics</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{stats?.total_participants || 0}</div>
                <div className="stat-label">Total Joined</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{stats?.current_participants || 0}</div>
                <div className="stat-label">Currently In</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{formatDuration(stats?.avg_duration)}</div>
                <div className="stat-label">Avg Duration</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{formatDuration(stats?.max_duration)}</div>
                <div className="stat-label">Max Duration</div>
              </div>
            </div>
          </motion.div>

          {/* Participants List - Full Width */}
          <motion.div
            className="detail-card card-participants detail-card-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <h3>👥 Participant History ({participants.length})</h3>
            {participants.length === 0 ? (
              <p className="empty-message">No participants yet</p>
            ) : (
              <div className="participants-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Joined</th>
                      <th>Left</th>
                      <th>Duration</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((participant) => (
                      <tr key={participant.id}>
                        <td style={{fontWeight: 600, color: 'var(--text-primary)'}}>{participant.name || 'Guest'}</td>
                        <td>
                          {participant.is_host ? (
                            <span className="role-badge host">👑 Host</span>
                          ) : (
                            <span className="role-badge participant">👤 Participant</span>
                          )}
                        </td>
                        <td>{new Date(participant.joined_at).toLocaleTimeString()}</td>
                        <td>{participant.left_at ? new Date(participant.left_at).toLocaleTimeString() : '—'}</td>
                        <td>{formatDuration(participant.duration)}</td>
                        <td>
                          {participant.left_at ? (
                            <span className="status-left">Left</span>
                          ) : (
                            <span className="status-active">🟢 Active</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          {/* Recordings - Full Width */}
          <motion.div
            className="detail-card card-recordings detail-card-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <h3>📹 Recordings ({recordings.length})</h3>
            {recordings.length === 0 ? (
              <p className="empty-message">No recordings for this meeting</p>
            ) : (
              <div className="recordings-list-simple">
                {recordings.map((recording) => (
                  <div key={recording.id} className="recording-item-simple">
                    <div className="recording-info-simple">
                      <span className="recording-date">
                        📅 {new Date(recording.created_at).toLocaleString()}
                      </span>
                      <span className="recording-duration">
                        ⏱️ {formatDuration(recording.duration)}
                      </span>
                      <span className="recording-size">
                        💾 {(recording.size / 1024).toFixed(2)} KB
                      </span>
                    </div>
                    <button
                      onClick={() => navigate('/recordings')}
                      className="btn-view-recording"
                    >
                      🎧 View
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {meeting.status !== 'ended' && (
          <motion.div
            className="action-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <motion.button
              onClick={() => navigate(`/meeting/${id}`)}
              className="btn-join-large"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              🎙️ Join Meeting
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default MeetingDetails;
